import * as React from 'react';
import { styled } from '@mui/material/styles';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell, { tableCellClasses } from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import { ResponseApi } from '../../types/Response/ResponseApi';
import { Categoria, GetVoidCategoria } from '../../types/Categoria/Categoria';
import { Box, Dialog, DialogContent, DialogTitle, Grid, IconButton, Pagination, Tooltip } from '@mui/material';
import { Iconify } from '../../common/iconify';
import CategoriasService from '../../services/CategoriasService';
import SweetAlertUtil from '../../utils/SweetAlert';
import { CategoriaForm } from './CategoriaForm';

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
  },
  // hide last border
  '&:last-child td, &:last-child th': {
    border: 0,
  },
}));

interface Props {
  data: ResponseApi<Categoria>,
  onUpdate?: (page: number, pageSize: number) => void,
}

export const CategoriaTable: React.FC<Props> = ({ data, onUpdate }) => {
  const [rows, setRows] = React.useState<Array<Categoria>>([]);
  const [page, setPage] = React.useState(data.page ?? 1);
  const [totalPage, setTotalPage] = React.useState(data.totalPages ?? 1);
  const [pageSize, setPageSize] = React.useState(10);
  const [openC, setOpenC] = React.useState(false);
  const [categoria, setCategoria] = React.useState(GetVoidCategoria());

  async function ActivarCategoria(id: number) {
    SweetAlertUtil.showLoading('Espere...');
    try {
      const response = await CategoriasService.ActivarCategoria(id);
      const data = response;
      console.log(data)
      SweetAlertUtil.hideLoading();
      if (data.status.isSuccessfully) {
        SweetAlertUtil.alertaExito('Ok', 'Categoría activada.');
        if (onUpdate) {
          onUpdate(page, pageSize)
        }
      } else {
        SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problemas.');
      }
    } catch (error) {
      SweetAlertUtil.hideLoading();
      console.error(error)
      SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problemas.');
    }
  }

  async function InactivarCategoria(id: number) {
    SweetAlertUtil.showLoading('Espere...');
    try {
      const response = await CategoriasService.InactivarCategoria(id);
      const data = response;
      SweetAlertUtil.hideLoading();
      if (data.status.isSuccessfully) {
        SweetAlertUtil.alertaExito('Ok', 'Categoría inactivada.');
        if (onUpdate) {
          onUpdate(page, pageSize)
        }
      } else {
        SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problemas.');
      }
    } catch (error) {
      SweetAlertUtil.hideLoading();
      console.error(error)
      SweetAlertUtil.alertaAdvertencia('Alerta', 'Ocurrio un problemas.');
    }
  }

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    if (onUpdate) {
      onUpdate(value, pageSize)
    }
    setPage(value);
  };

  function SelectCategoria(obj: Categoria) {
    setCategoria(obj);
    handleClickOpenC()
  }

  const handleClickOpenC = () => {
    setOpenC(true);
  };

  const handleCloseC = () => {
    setOpenC(false);
  };

  const handleResponse = (resp: any) => {
    handleCloseC()
    if (onUpdate) {
      onUpdate(page, pageSize)
    }
  };

  React.useEffect(() => {
    if (data?.listData) {
      setRows(data.listData);
    }
    if (data?.totalPages) {
      setTotalPage(data.totalPages);
    }
  }, [data]);

  return (
    <Paper sx={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 2,
      gap: 2 // Espaciado entre elementos
    }}>
      {totalPage > 1 ? <Pagination count={totalPage} page={page} onChange={handlePageChange} showFirstButton showLastButton /> : <></>}
      <TableContainer component={Paper}>
        <Table sx={{ minWidth: 700 }} aria-label="customized table">
          <TableHead>
            <TableRow>
              <StyledTableCell>Nombre</StyledTableCell>
              <StyledTableCell>Descripción</StyledTableCell>
              <StyledTableCell>Estado</StyledTableCell>
              <StyledTableCell>Opciones</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((row) => (
              <StyledTableRow key={row.idCategoria}>
                <StyledTableCell component="th" scope="row">
                  {row.txNombreCategoria}
                </StyledTableCell>
                <StyledTableCell>{row.txtDescripcion}</StyledTableCell>
                <StyledTableCell>{row.bdEstadoRegistro ? "Activo" : "Inactivo"}</StyledTableCell>
                <StyledTableCell>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={6}>
                      {row.bdEstadoRegistro ?
                        <Tooltip title={'Inactivar'} placement='top' arrow>
                          <IconButton sx={{ color: '#61AFFE' }} onClick={() => { InactivarCategoria(row.idCategoria) }}>
                            <Iconify icon={'system-uicons:eye-no'} />
                          </IconButton>
                        </Tooltip>
                        :
                        <Tooltip title={'Activar'} placement='top' arrow>
                          <IconButton sx={{ color: '#61AFFE' }} onClick={() => { ActivarCategoria(row.idCategoria) }} >
                            <Iconify icon={'mdi-light:eye'} />
                          </IconButton>
                        </Tooltip>
                      }
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Tooltip title={'Editar'} placement='top' arrow>
                        <IconButton sx={{ color: '#61AFFE' }} onClick={() => { SelectCategoria(row) }} >
                          <Iconify icon={'material-symbols:edit'} />
                        </IconButton>
                      </Tooltip>
                    </Grid>
                  </Grid>
                </StyledTableCell>
              </StyledTableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {totalPage > 1 ? <Pagination count={totalPage} page={page} onChange={handlePageChange} showFirstButton showLastButton /> : <></>}
      <Dialog
        open={openC}
        onClose={handleCloseC}
      >
        <DialogTitle id="alert-dialog-title">
          {"Editar Categoría"}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 1 }} display="flex" justifyContent="center" alignItems="center" flexDirection="column">
            <CategoriaForm categoria={categoria} onResponse={handleResponse} />
          </Box>
        </DialogContent>
      </Dialog>
    </Paper>
  );
}